import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { Block } from '@klp/design/Block';
import { Region } from '../classes/Region';
import { useRegion } from '../store';
import { useGetRegionListQuery } from '../services/rtkApi';
import { useCurrentUser } from '@klp/auth';

interface IRegionSelectorProps {
    onRegionChanged?: (region: Region) => void;
    // Translation key for the label
    label?: string;
    // Translation key for the option that selects all regions
    allLabel?: string;
    disabled?: boolean;
}

const emptyRegion = Region.empty();

const useStyles = makeStyles((theme) => ({
    selector: {
        width: '100%',
        minWidth: theme.spacing(25),
    },
}));

export const RegionSelector: React.FC<IRegionSelectorProps> = ({
    onRegionChanged,
    label = 'region.select',
    allLabel = 'region.all',
    disabled,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { region, selectRegion } = useRegion();
    const { currentUser } = useCurrentUser();

    // The list is cached, and shared by every selector
    const { data: regionResponse, isLoading: regionListLoading } =
        useGetRegionListQuery();

    const regionList = regionResponse?.regionList;

    // Runs when the list is available, also when it comes from the cache.
    // Only preselect when nothing is stored, so that «all» stays selected.
    useEffect(() => {
        if (!regionList || !currentUser?.portfolioRegion) return;
        if (Region.storedSelectedRegion !== undefined) return;

        const regionToPreselect = regionList.find(
            (entry) => entry.name === currentUser.portfolioRegion,
        );

        if (regionToPreselect) {
            selectRegion(regionToPreselect);
        }
    }, [regionList, currentUser?.portfolioRegion, region, selectRegion]);

    const handleRegionSelect = (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const name = event.target.value;

        const selectedRegion =
            regionList?.find((entry) => entry.name === name) ?? emptyRegion;

        selectRegion(selectedRegion);

        if (onRegionChanged) {
            onRegionChanged(selectedRegion);
        }
    };

    const renderMenuItem = (entry: Region, index: number) => (
        <MenuItem key={`${entry.name}-${index}`} value={entry.name}>
            {entry.name}
        </MenuItem>
    );

    const selectedRegion = region;

    const selectedValue =
        selectedRegion &&
        regionList?.find((entry) => entry.name === selectedRegion.name)
            ? selectedRegion.name
            : t(allLabel);

    return (
        <Block marginBottom="tiny">
            <FormControl
                className={classes.selector}
                data-testid="RegionSelector"
                disabled={disabled}
            >
                <InputLabel id="RegionSelect__Label">{t(label)}</InputLabel>
                <Select
                    labelId="RegionSelect__Label"
                    // Only used by the outlined variant, to make room for the label in the border
                    label={t(label)}
                    id="RegionSelect"
                    value={selectedValue}
                    onChange={handleRegionSelect}
                    data-testid="RegionSelect"
                >
                    <MenuItem
                        key={t(allLabel)}
                        value={t(allLabel) as string}
                        className="menuItem"
                    >
                        {t(allLabel)}
                    </MenuItem>
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <MenuItem key="divider" value="divider" disabled={true}>
                        - - - - - - - - - -
                    </MenuItem>
                    {!regionListLoading &&
                        regionList?.map((entry, index) =>
                            renderMenuItem(entry, index),
                        )}
                </Select>
            </FormControl>
        </Block>
    );
};
